import { useCallback, useEffect, useMemo, useRef } from "react";

/** How close to an edge the pointer has to be before the chart scrolls. */
export const EDGE_SCROLL_ZONE_PX = 48;
/** The fastest step, reached with the pointer at (or past) the edge itself. */
export const EDGE_SCROLL_MAX_STEP_PX = 18;

/**
 * How far to scroll along one axis this frame: faster the deeper the pointer
 * is into the zone at either end, nothing in between. `min` and `max` are the
 * usable edges — for the chart that means below the sticky axis header and
 * right of the sticky title column, which cover the scroller's real edges.
 */
export function edgeScrollStep(position: number, min: number, max: number): number {
  const low = min + EDGE_SCROLL_ZONE_PX;
  const high = max - EDGE_SCROLL_ZONE_PX;
  if (position < low) {
    return -EDGE_SCROLL_MAX_STEP_PX * Math.min(1, (low - position) / EDGE_SCROLL_ZONE_PX);
  }
  if (position > high) {
    return EDGE_SCROLL_MAX_STEP_PX * Math.min(1, (position - high) / EDGE_SCROLL_ZONE_PX);
  }
  return 0;
}

export interface EdgeAutoScrollOptions {
  /** Where the pointer is, in client coordinates. Null ends the loop. */
  pointer: () => { x: number; y: number } | null;
  /** Height of the sticky chrome over the scroller's top edge. */
  insetTop: number;
  /** Width of the sticky chrome over the scroller's left edge. */
  insetLeft?: number;
  /** Scroll sideways as well as up and down. */
  horizontal?: boolean;
  /**
   * Called after a step that actually moved the scroller. A pointer held
   * still sends no events, so whatever the gesture derives from the pointer's
   * position over the content has to be re-derived here.
   */
  onScrolled?: () => void;
}

/**
 * Scroll the chart while a drag is held near one of its edges, so a row can
 * be carried — or a link drawn — to something that does not fit on screen.
 * One rAF loop per gesture; `stop` (and unmount) ends it.
 */
export function useEdgeAutoScroll(scrollRef: React.RefObject<HTMLElement>) {
  const frame = useRef<number | null>(null);

  const stop = useCallback(() => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
  }, []);

  useEffect(() => stop, [stop]);

  const start = useCallback(
    (options: EdgeAutoScrollOptions) => {
      if (frame.current !== null) return;
      const tick = () => {
        const at = options.pointer();
        const el = scrollRef.current;
        if (!at || !el) {
          frame.current = null;
          return;
        }
        const rect = el.getBoundingClientRect();
        const dy = edgeScrollStep(at.y, rect.top + options.insetTop, rect.bottom);
        const dx = options.horizontal
          ? edgeScrollStep(at.x, rect.left + (options.insetLeft ?? 0), rect.right)
          : 0;
        if (dx !== 0 || dy !== 0) {
          const beforeTop = el.scrollTop;
          const beforeLeft = el.scrollLeft;
          el.scrollTop += dy;
          el.scrollLeft += dx;
          if (el.scrollTop !== beforeTop || el.scrollLeft !== beforeLeft) options.onScrolled?.();
        }
        frame.current = requestAnimationFrame(tick);
      };
      frame.current = requestAnimationFrame(tick);
    },
    [scrollRef]
  );

  return useMemo(() => ({ start, stop }), [start, stop]);
}
